/**
 * Markdown-Darstellung mit Kontext: Erwähnungs-Chips (Profil-Popout, Kanal-Navigation), eigene/animierte Emojis,
 * Spoiler. Das HTML kommt ausschließlich aus `renderMarkdown` (escaped + DOMPurify, siehe lib/markdown.ts).
 */
import { isDm } from '@lagerfeuer/shared';
import { useEffect, useMemo, useRef, type KeyboardEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { anchorFrom } from '../../components/Popover';
import { renderMarkdown, type MarkdownContext } from '../../lib/markdown';
import { pathForChannel } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { dmName, memberColor } from '../../store/selectors';
import { toast } from '../../store/ui';
import { findEmoji, requestEmoji, useEmojiCache } from './emojiCache';
import './messages.css';

const EMPTY_ROLES: never[] = [];

/** Baut den Auflösungs-Kontext für Erwähnungen/Emojis aus dem Store. */
export function useMarkdownContext(serverId?: string | null): MarkdownContext {
  const users = useAppStore((s) => s.users);
  const members = useAppStore((s) => (serverId ? s.members[serverId] : undefined));
  const roles = useAppStore((s) => (serverId ? s.roles[serverId] : undefined)) ?? EMPTY_ROLES;
  const channels = useAppStore((s) => s.channels);
  const emojis = useAppStore((s) => s.emojis);
  const meId = useAppStore((s) => s.me?.id ?? null);
  const myRoleIds = useAppStore((s) => (serverId ? s.servers[serverId]?.me.roleIds : undefined));
  const animate = useAppStore((s) => s.settings?.autoplayGifs ?? true);
  const cache = useEmojiCache((s) => s.byId);
  return useMemo<MarkdownContext>(
    () => ({
      meId,
      myRoleIds: myRoleIds ?? [],
      animateEmojis: animate,
      resolveUser: (id) => {
        const m = members?.[id];
        const u = m?.user ?? users[id];
        if (!u) return null;
        return { name: m?.nickname || u.displayName || u.username };
      },
      resolveRole: (id) => {
        const r = roles.find((x) => x.id === id);
        return r ? { name: r.name, color: memberColor([r], [r.id]) } : null;
      },
      resolveChannel: (id) => {
        const c = channels[id];
        if (!c) return null;
        return { name: isDm(c.type) ? dmName(c, meId ?? undefined) : (c.name ?? 'unbekannt'), type: c.type };
      },
      resolveEmoji: (id) => {
        const e = findEmoji({ emojis }, id) ?? cache[id] ?? null;
        return e ? { url: e.url } : null;
      },
    }),
    [users, members, roles, channels, emojis, meId, myRoleIds, animate, cache],
  );
}

interface MarkdownProps {
  content: string;
  serverId?: string | null;
  inline?: boolean;
  /** Nur Emojis → große Darstellung. */
  jumbo?: boolean;
  className?: string;
  testId?: string;
}

/** Bereinigtes Markdown mit Klick-/Tastatur-Handling für Erwähnungen und Spoiler. */
export function Markdown({ content, serverId, inline, jumbo, className = '', testId }: MarkdownProps) {
  const ctx = useMarkdownContext(serverId);
  const html = useMemo(() => renderMarkdown(content, ctx, { inline }), [content, ctx, inline]);
  const ref = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  // Unbekannte eigene Emojis nachladen (fremde Server).
  useEffect(() => {
    ref.current?.querySelectorAll<HTMLElement>('.emoji-unresolved[data-emoji-id]').forEach((el) => {
      const id = el.dataset.emojiId;
      if (id) requestEmoji(id);
    });
  }, [html]);

  const activate = (target: EventTarget | null): boolean => {
    const el = target instanceof Element ? target : null;
    if (!el) return false;
    const spoiler = el.closest<HTMLElement>('.md-spoiler');
    if (spoiler && spoiler.dataset.spoiler === 'hidden') {
      spoiler.dataset.spoiler = 'shown';
      spoiler.removeAttribute('role');
      spoiler.removeAttribute('aria-label');
      return true;
    }
    const user = el.closest<HTMLElement>('[data-user-id]');
    if (user?.dataset.userId) {
      overlays.profile(user.dataset.userId, anchorFrom(user), serverId ?? null);
      return true;
    }
    const chan = el.closest<HTMLElement>('.channel-mention[data-channel-id]');
    if (chan?.dataset.channelId) {
      const ch = useAppStore.getState().channels[chan.dataset.channelId];
      if (ch) void navigate(pathForChannel(ch));
      else toast.info('Kanal nicht verfügbar', 'Den Kanal gibt es nicht mehr oder du kannst ihn nicht sehen.');
      return true;
    }
    return false;
  };

  const onClick = (e: MouseEvent) => {
    if (activate(e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target !== e.currentTarget && activate(e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const cls = `markdown ${inline ? 'markdown-inline' : ''} ${jumbo ? 'md-jumbo' : ''} ${className}`.trim();
  if (inline) {
    return <span ref={ref as React.RefObject<HTMLSpanElement>} className={cls} data-testid={testId} onClick={onClick} onKeyDown={onKeyDown} dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return <div ref={ref as React.RefObject<HTMLDivElement>} className={cls} data-testid={testId} onClick={onClick} onKeyDown={onKeyDown} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Inline-Markdown (Kanalthema, Vorschauen). Signatur wird von der Shell genutzt. */
export function MarkdownInline({ content, serverId, block }: { content: string; serverId?: string | null; block?: boolean }) {
  return <Markdown content={content} serverId={serverId} inline={!block} />;
}
